// src/hooks/useRole.ts
import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { StatusLaporan } from '../types';

export interface RoleInfo {
    role: string;
    displayName: string;
    isAdmin: boolean;
    isRektor: boolean;
    isWarek: boolean;
    isDekan: boolean;
    isKalem: boolean;
    isKaprodi: boolean;
    isKabag: boolean;
    isKaTU: boolean;
    isLPM: boolean;
    isObserver: boolean;
    // Derived capabilities
    canAccessReportBuilder: boolean;
    canSeeAllUnits: boolean;
    canValidateL1: boolean;
    canValidateL2: boolean;
    canManageRapim: boolean;
    canCreateLaporan: boolean;
    canEditLaporan: (status: StatusLaporan) => boolean;
    canArchive: boolean;
}

const EDITABLE_STATUSES: StatusLaporan[] = ['draf', 'ditolak_l1', 'ditolak_l2'];

export function useRole(): RoleInfo {
    const user = useAuthStore((s) => s.user);

    return useMemo(() => {
        const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
        const rawRoleName = (user?.role?.name || storedRole || '').toString();
        const roleName = rawRoleName.toLowerCase();
        const displayName = (user?.role?.display_name || rawRoleName || '').toString();
        const displayNameLower = displayName.toLowerCase();

        const isAdmin = roleName === 'admin' || displayNameLower === 'administrator';
        const isRektor = roleName === 'rektor';
        const isWarek = [
            'warek', 'warek1', 'warek2', 'warek3', 'warek4',
            'warek_1', 'warek_2', 'warek_3', 'warek_4',
            'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4'
        ].includes(roleName);
        const isDekan = roleName === 'dekan';
        const isKalem = roleName === 'kalem';
        const isKaprodi = roleName === 'kaprodi' || roleName === 'kepala_program_studi';
        const isKabag = roleName === 'kabag' || roleName === 'kepala_bagian';
        const isKaTU = roleName === 'ka_tu' || roleName === 'kepala_tata_usaha';
        const isLPM = roleName === 'lpm';
        const isObserver = isLPM || roleName === 'senat';

        return {
            role: roleName,
            displayName,
            isAdmin,
            isRektor,
            isWarek,
            isDekan,
            isKalem,
            isKaprodi,
            isKabag,
            isKaTU,
            isLPM,
            isObserver,

            // Report Builder permissions
            canAccessReportBuilder: isAdmin || isRektor || isObserver || isWarek || isDekan || isKalem,
            canSeeAllUnits: isAdmin || isRektor || isObserver || isWarek,

            // Dekan and Kalem can validate Level 1
            canValidateL1: isDekan || isKalem || isAdmin,
            // Warek can validate Level 2
            canValidateL2: isWarek || isAdmin,
            // Rektor, Warek, Admin can manage RAPIM
            canManageRapim: isRektor || isWarek || isAdmin,
            // Unit heads can create laporan
            canCreateLaporan: isKaprodi || isKabag || isKaTU || isKalem || isAdmin,
            // Can edit only if status is editable AND user is unit head / admin
            canEditLaporan: (status: StatusLaporan) => {
                const canByRole = isAdmin || isKaprodi || isKabag || isKaTU || isKalem;
                return canByRole && EDITABLE_STATUSES.includes(status);
            },
            // Only admin and LPM can archive
            canArchive: isAdmin || isLPM,
        };
    }, [user]);
}
